// A workload's resources over time: CPU and memory from the runtime's
// samples, as a plain inline SVG time series on the system's tokens.

import { bytes } from "../../ui/components.tsx";
import type { Sample } from "../../wire/workloads.ts";

const W = 640;
const H = 150;
const PAD = { l: 44, r: 44, t: 10, b: 22 };

interface Series {
  name: string;
  cls: "" | "second";
  values: number[];
  format: (v: number) => string;
}

function path(values: number[], xs: number[], max: number): string {
  if (values.length === 0 || max <= 0) {
    return "";
  }
  const innerH = H - PAD.t - PAD.b;
  return values
    .map((v, i) => {
      const x = xs[i] ?? PAD.l;
      const y = PAD.t + innerH - (Math.min(v, max) / max) * innerH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function SamplesChart({ samples }: { samples: Sample[] }) {
  const sorted = [...samples].sort((a, b) => a.at.localeCompare(b.at));
  if (sorted.length < 2) {
    return (
      <p className="muted small">
        Not enough measured yet to draw; CPU and memory are sampled on a fixed interval.
      </p>
    );
  }
  const t0 = new Date(sorted[0]?.at ?? "").getTime();
  const t1 = new Date(sorted[sorted.length - 1]?.at ?? "").getTime();
  const span = Math.max(1, t1 - t0);
  const innerW = W - PAD.l - PAD.r;
  const xs = sorted.map((s) => PAD.l + ((new Date(s.at).getTime() - t0) / span) * innerW);

  const cpu: Series = {
    name: "CPU %",
    cls: "",
    values: sorted.map((s) => s.cpu_percent),
    format: (v) => `${v.toFixed(0)}%`,
  };
  const mem: Series = {
    name: "memory",
    cls: "second",
    values: sorted.map((s) => s.memory_bytes),
    format: bytes,
  };
  const cpuMax = Math.max(100, ...cpu.values);
  const memMax = Math.max(1, ...mem.values);

  return (
    <div className="stack">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="CPU and memory over time">
        <line className="axis" x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} />
        <line className="axis" x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} />
        <line className="axis" x1={W - PAD.r} y1={PAD.t} x2={W - PAD.r} y2={H - PAD.b} />
        <path className="line" d={path(cpu.values, xs, cpuMax)} />
        <path className="line second" d={path(mem.values, xs, memMax)} />
        <text x={PAD.l - 4} y={PAD.t + 8} textAnchor="end">
          {cpu.format(cpuMax)}
        </text>
        <text x={PAD.l - 4} y={H - PAD.b} textAnchor="end">
          0
        </text>
        <text x={W - PAD.r + 4} y={PAD.t + 8}>
          {mem.format(memMax)}
        </text>
        <text x={W - PAD.r + 4} y={H - PAD.b}>
          0
        </text>
        <text x={PAD.l} y={H - 6}>
          {clock(sorted[0]?.at ?? "")}
        </text>
        <text x={W - PAD.r} y={H - 6} textAnchor="end">
          {clock(sorted[sorted.length - 1]?.at ?? "")}
        </text>
      </svg>
      <div className="legend">
        <span>
          <span className="swatch" />
          CPU, percent of its virtual CPUs
        </span>
        <span>
          <span className="swatch second" />
          memory used
        </span>
        <span className="muted">{sorted.length} measurements</span>
      </div>
    </div>
  );
}
